import React, { memo, useState, useRef, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { MdRefresh, MdChevronRight, MdExitToApp, MdVideocam, MdSettings } from 'react-icons/md'
import { KioskModeContext } from '../../../context/Shared/KioskMode.context'
import { theme } from '../../../styles/theme.style'
import KioskFeedPreview from '../KioskFeedPreview/KioskFeedPreview.component'
import KioskSettings from '../KioskSettings/KioskSettings.component'
import styles from './KioskWaiting.module.scss'

const KioskWaiting = ({ onlineStatus, deviceName }) => {
  const { t } = useTranslation()
  const { exitKioskMode } = useContext(KioskModeContext)
  const [showArrow, setShowArrow] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const hideTimerRef = useRef(null)

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }

  const startHideTimer = () => {
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      setShowArrow(false)
      setShowMenu(false)
    }, 5000)
  }

  useEffect(() => {
    return () => clearHideTimer()
  }, [])

  const handleLogoClick = () => {
    if (showArrow) {
      setShowArrow(false)
      setShowMenu(false)
      clearHideTimer()
    } else {
      setShowArrow(true)
      startHideTimer()
    }
  }

  const handleArrowClick = (e) => {
    e.stopPropagation()
    setShowMenu((prev) => !prev)
    startHideTimer()
  }

  const handleExitKiosk = (e) => {
    e.stopPropagation()
    setShowArrow(false)
    setShowMenu(false)
    clearHideTimer()
    exitKioskMode()
  }

  const handleTestFeeds = (e) => {
    e.stopPropagation()
    setShowArrow(false)
    setShowMenu(false)
    clearHideTimer()
    setShowPreview(true)
  }

  const handleSettings = (e) => {
    e.stopPropagation()
    setShowArrow(false)
    setShowMenu(false)
    clearHideTimer()
    setShowSettings(true)
  }

  if (showPreview) {
    return <KioskFeedPreview onClose={() => setShowPreview(false)} />
  }

  if (showSettings) {
    return <KioskSettings onClose={() => setShowSettings(false)} />
  }

  return (
    <div className={styles.kioskWaiting}>
      <div className={styles.kioskWaiting__content}>
        <div className={styles.kioskWaiting__logoWrapper}>
          <img
            src={theme.LOGO_WHITE}
            alt="TeleVU"
            className={styles.kioskWaiting__logo}
            onClick={handleLogoClick}
          />
          {showArrow && (
            <button
              className={styles.kioskWaiting__arrowButton}
              onClick={handleArrowClick}
              aria-label={t('Menu')}
            >
              <MdChevronRight />
            </button>
          )}
          {showMenu && (
            <div className={styles.kioskWaiting__menu}>
              <button
                className={styles.kioskWaiting__menuButton}
                onClick={handleTestFeeds}
              >
                <MdVideocam />
                <span>{t('Test Video Feeds')}</span>
              </button>
              <button
                className={styles.kioskWaiting__menuButton}
                onClick={handleSettings}
              >
                <MdSettings />
                <span>{t('Settings')}</span>
              </button>
              <button
                className={styles.kioskWaiting__menuButton}
                onClick={handleExitKiosk}
              >
                <MdExitToApp />
                <span>{t('Exit Kiosk Mode')}</span>
              </button>
            </div>
          )}
        </div>
        {deviceName && (
          <div className={styles.kioskWaiting__deviceName}>
            {deviceName}
          </div>
        )}
        <div className={styles.kioskWaiting__statusContainer}>
          <div
            className={`${styles.kioskWaiting__statusIndicator} ${
              onlineStatus
                ? styles['kioskWaiting__statusIndicator--online']
                : styles['kioskWaiting__statusIndicator--offline']
            }`}
          />
          <span className={styles.kioskWaiting__statusText}>
            {onlineStatus ? t('Online') : t('Connecting...')}
          </span>
        </div>
        <p className={styles.kioskWaiting__message}>
          {onlineStatus
            ? t('Waiting for call...')
            : t('Establishing connection...')}
        </p>
      </div>
      <button
        className={styles.kioskWaiting__refreshButton}
        onClick={() => window.location.reload()}
        aria-label={t('Refresh')}
      >
        <MdRefresh />
      </button>
    </div>
  )
}

KioskWaiting.propTypes = {
  onlineStatus: PropTypes.bool,
  deviceName: PropTypes.string
}

export default memo(KioskWaiting)
